// frontend/src/modules/Confirmations/ConfirmationDetail.tsx
import { useState, useEffect } from 'react';
import {
  Card, Button, Stack, Group, Text, Grid, Badge, Loader, Center,
  ThemeIcon, Paper, Container, ActionIcon
} from '@mantine/core';
import {
  IconArrowLeft, IconEdit, IconTrash, IconCalendar, IconUser,
  IconHome, IconPhone, IconMail, IconClock, IconFileCheck, IconNote,
  IconPlane, IconCar, IconBolt, IconDroplet, IconCurrencyBaht,
  IconUserCircle, IconBabyCarriage, IconCheck, IconCash, IconDownload
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useMediaQuery } from '@mantine/hooks';
import { confirmationsApi, Confirmation } from '@/api/confirmations.api';
import CreateConfirmationModal from './components/CreateConfirmationModal';
import PaymentManagementModal from './components/PaymentManagementModal';
import dayjs from 'dayjs';

const ConfirmationDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [loading, setLoading] = useState(true);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);

  useEffect(() => {
    if (id) fetchConfirmation();
  }, [id]);

  const fetchConfirmation = async () => {
    try {
      setLoading(true);
      const response = await confirmationsApi.getById(Number(id));
      setConfirmation(response.data.data);
    } catch {
      notifications.show({ title: t('errors.generic'), message: 'Failed to load confirmation', color: 'red' });
      navigate('/confirmations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    modals.openConfirmModal({
      title: t('common.confirmDelete'),
      children: <Text size="sm">{t('common.deleteConfirmation')} <strong>{confirmation?.confirmation_number}</strong>?</Text>,
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await confirmationsApi.delete(Number(id));
          notifications.show({ title: t('common.success'), message: 'Deleted', color: 'green' });
          navigate('/confirmations');
        } catch { notifications.show({ title: t('errors.generic'), message: 'Failed to delete', color: 'red' }); }
      }
    });
  };

  if (loading) return <Center p="xl"><Loader size="lg" /></Center>;
  if (!confirmation) return <Center p="xl"><Text>Not found</Text></Center>;

  const c = confirmation;
  const nights = c.arrival_date && c.departure_date
    ? dayjs(c.departure_date).diff(dayjs(c.arrival_date), 'day')
    : 0;

  return (
    <Container size="xl" p={isMobile ? 'sm' : 'md'}>
      <Stack gap="lg">
        {/* Header */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" wrap="wrap">
            <Group gap="sm">
              <ActionIcon variant="light" onClick={() => navigate('/confirmations')}>
                <IconArrowLeft size={18} />
              </ActionIcon>
              <ThemeIcon size="xl" radius="md" variant="gradient" gradient={{ from: 'teal', to: 'cyan' }}>
                <IconFileCheck size={28} />
              </ThemeIcon>
              <div>
                <Text fw={600} size="xl">{c.confirmation_number}</Text>
                <Group gap="xs">
                  <Badge color={c.status === 'active' ? 'green' : 'gray'} variant="light" size="sm">{c.status}</Badge>
                  <Badge color={c.payment_status === 'success' ? 'green' : 'orange'} variant="light" size="sm">
                    {c.payment_status === 'success' ? 'Paid' : 'Pending'}
                  </Badge>
                </Group>
              </div>
            </Group>
            <Group gap="xs">
              <Button variant="gradient" gradient={{ from: 'teal', to: 'cyan' }} leftSection={<IconCash size={16} />} onClick={() => setPaymentModalVisible(true)}>Payments</Button>
              <Button variant="light" color="violet" leftSection={<IconDownload size={16} />} onClick={async () => {
                try {
                  const res = await confirmationsApi.downloadPDF(Number(id));
                  const blob = new Blob([res.data], { type: 'application/pdf' });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a'); link.href = url;
                  link.download = `${confirmation?.confirmation_number || 'confirmation'}.pdf`;
                  document.body.appendChild(link); link.click(); document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                } catch { notifications.show({ title: 'Error', message: 'Failed to download PDF', color: 'red' }); }
              }}>PDF</Button>
              <Button variant="light" leftSection={<IconEdit size={16} />} onClick={() => setEditModalVisible(true)}>{t('common.edit')}</Button>
              <Button variant="light" color="red" leftSection={<IconTrash size={16} />} onClick={handleDelete}>{t('common.delete')}</Button>
            </Group>
          </Group>
        </Card>

        {/* Agent */}
        {c.agent_name && (
          <Paper p="md" radius="md" withBorder>
            <Group gap="sm">
              <ThemeIcon size="lg" variant="light" color="violet"><IconUserCircle size={20} /></ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Agent</Text>
                <Text fw={600}>{c.agent_name}</Text>
              </div>
            </Group>
          </Paper>
        )}

        {/* Dates */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Paper p="md" radius="md" withBorder>
              <Group gap="sm">
                <ThemeIcon size="lg" variant="light" color="green"><IconCalendar size={20} /></ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed">Arrival</Text>
                  <Text fw={600}>{c.arrival_date ? dayjs(c.arrival_date).format('DD MMM YYYY') : '—'}</Text>
                  <Text size="xs" c="dimmed">{c.arrival_time || '15:00'}</Text>
                </div>
              </Group>
            </Paper>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Paper p="md" radius="md" withBorder>
              <Group gap="sm">
                <ThemeIcon size="lg" variant="light" color="red"><IconCalendar size={20} /></ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed">Departure</Text>
                  <Text fw={600}>{c.departure_date ? dayjs(c.departure_date).format('DD MMM YYYY') : '—'}</Text>
                  <Text size="xs" c="dimmed">{c.departure_time || '12:00'}</Text>
                </div>
              </Group>
            </Paper>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Paper p="md" radius="md" withBorder>
              <Group gap="sm">
                <ThemeIcon size="lg" variant="light" color="blue"><IconClock size={20} /></ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed">Duration</Text>
                  <Text fw={600}>{nights} nights</Text>
                  <Text size="xs" c="dimmed">Check-in: {c.check_in_time || '15:00'} / Check-out: {c.check_out_time || '12:00'}</Text>
                </div>
              </Group>
            </Paper>
          </Grid.Col>
        </Grid>

        {/* Property & Contact */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text fw={600} mb="md"><IconHome size={16} style={{ verticalAlign: 'middle' }} /> Property & Contact</Text>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Text size="sm" fw={500}>{c.property_name || '—'}</Text>
              <Text size="xs" c="dimmed">{c.property_address || '—'}</Text>
              {c.room_type && <Badge size="xs" variant="light" mt="xs">{c.room_type}</Badge>}
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              {c.from_company_name && <Text size="sm" fw={500} mb="xs">{c.from_company_name}</Text>}
              <Group gap="sm" mb="xs"><IconPhone size={14} /><Text size="sm">{c.from_telephone || '—'}</Text></Group>
              <Group gap="sm"><IconMail size={14} /><Text size="sm">{c.from_email || '—'}</Text></Group>
            </Grid.Col>
          </Grid>
        </Card>

        {/* Booking Details */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text fw={600} mb="md"><IconCurrencyBaht size={16} style={{ verticalAlign: 'middle' }} /> Booking Details</Text>
          <Grid>
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <Text size="xs" c="dimmed">Rate ({c.rate_type === 'monthly' ? 'monthly' : c.rate_type === 'for_period' ? 'for period' : 'per night'})</Text>
              <Text fw={500}>{c.rate_amount ? `${c.rate_amount.toLocaleString()} ${c.currency || 'THB'}` : '—'}</Text>
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <Text size="xs" c="dimmed">Deposit</Text>
              <Text fw={500}>{c.deposit_amount ? `${c.deposit_amount.toLocaleString()} ${c.currency || 'THB'}` : '—'}</Text>
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 2 }}>
              <Text size="xs" c="dimmed">Rooms</Text>
              <Text fw={500}>{c.num_rooms || 1}</Text>
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 2 }}>
              <Text size="xs" c="dimmed">Guests</Text>
              <Text fw={500}>{c.num_guests || 0}</Text>
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 2 }}>
              <Text size="xs" c="dimmed">Utilities</Text>
              <Group gap={4}>
                <IconBolt size={12} /><Text size="xs">{c.electricity_rate ?? 8}฿</Text>
                <IconDroplet size={12} /><Text size="xs">{c.water_rate ?? 30}฿</Text>
              </Group>
            </Grid.Col>
          </Grid>
        </Card>

        {/* Transfer Services */}
        {(c.pick_up_service || c.drop_off_service) && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text fw={600} mb="md"><IconCar size={16} style={{ verticalAlign: 'middle' }} /> Transfer Services</Text>
            <Grid>
              {c.pick_up_service && (
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Group gap="sm">
                    <ThemeIcon size="sm" variant="light" color="green"><IconCheck size={12} /></ThemeIcon>
                    <div>
                      <Text size="sm" fw={500}>Pick-up Service</Text>
                      {c.arrival_flight && <Text size="xs" c="dimmed"><IconPlane size={12} style={{ verticalAlign: 'middle' }} /> {c.arrival_flight}</Text>}
                    </div>
                  </Group>
                </Grid.Col>
              )}
              {c.drop_off_service && (
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Group gap="sm">
                    <ThemeIcon size="sm" variant="light" color="green"><IconCheck size={12} /></ThemeIcon>
                    <div>
                      <Text size="sm" fw={500}>Drop-off Service</Text>
                      {c.departure_flight && <Text size="xs" c="dimmed"><IconPlane size={12} style={{ verticalAlign: 'middle' }} /> {c.departure_flight}</Text>}
                    </div>
                  </Group>
                </Grid.Col>
              )}
            </Grid>
          </Card>
        )}

        {/* Guests */}
        {c.guests && c.guests.length > 0 && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text fw={600} mb="md"><IconUser size={16} style={{ verticalAlign: 'middle' }} /> Guests</Text>
            <Stack gap="sm">
              {c.guests.map((guest, idx) => (
                <Paper key={idx} p="sm" radius="sm" withBorder>
                  <Group gap="sm">
                    <ThemeIcon size="sm" variant="light"><IconUser size={14} /></ThemeIcon>
                    <div>
                      <Text size="sm" fw={500}>{guest.guest_name}</Text>
                      {guest.passport_number && <Text size="xs" c="dimmed">Passport: {guest.passport_number} {guest.passport_country && `(${guest.passport_country})`}</Text>}
                      {(guest.phone || guest.email) && <Text size="xs" c="dimmed">{[guest.phone, guest.email].filter(Boolean).join(' · ')}</Text>}
                    </div>
                  </Group>
                </Paper>
              ))}
            </Stack>
          </Card>
        )}

        {/* Children */}
        {c.children && c.children.length > 0 && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text fw={600} mb="md"><IconBabyCarriage size={16} style={{ verticalAlign: 'middle' }} /> Children</Text>
            <Stack gap="xs">
              {c.children.map((child, idx) => (
                <Group key={idx} gap="sm">
                  <Text size="sm">{child.child_name}</Text>
                  {child.child_age !== undefined && <Badge size="xs" variant="light">{child.child_age} years</Badge>}
                </Group>
              ))}
            </Stack>
          </Card>
        )}

        {/* Notice & Policy */}
        {(c.notice_content || c.cancellation_policy || c.welcome_message) && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text fw={600} mb="md"><IconNote size={16} style={{ verticalAlign: 'middle' }} /> Notice & Policy</Text>
            <Stack gap="md">
              {c.notice_content && (
                <div>
                  <Text size="xs" fw={600} c="dimmed" mb={4}>Notice</Text>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{c.notice_content}</Text>
                </div>
              )}
              {c.cancellation_policy && (
                <div>
                  <Text size="xs" fw={600} c="dimmed" mb={4}>Cancellation Policy</Text>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{c.cancellation_policy}</Text>
                </div>
              )}
              {c.welcome_message && (
                <div>
                  <Text size="xs" fw={600} c="dimmed" mb={4}>Welcome Message</Text>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{c.welcome_message}</Text>
                </div>
              )}
            </Stack>
          </Card>
        )}

        {/* Remarks */}
        {c.remarks && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group gap="sm" mb="xs"><IconNote size={16} /><Text fw={600}>Remarks</Text></Group>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{c.remarks}</Text>
          </Card>
        )}

        {/* Meta */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text size="xs" c="dimmed">
            Created: {dayjs(c.created_at).format('DD.MM.YYYY HH:mm')}
            {c.updated_at && ` · Updated: ${dayjs(c.updated_at).format('DD.MM.YYYY HH:mm')}`}
          </Text>
        </Card>
      </Stack>

      <CreateConfirmationModal
        visible={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onSuccess={() => { setEditModalVisible(false); fetchConfirmation(); }}
        mode="edit"
        confirmationId={Number(id)}
      />

      {confirmation && (
        <PaymentManagementModal
          opened={paymentModalVisible}
          onClose={() => setPaymentModalVisible(false)}
          confirmation={confirmation}
          onStatusChange={fetchConfirmation}
        />
      )}
    </Container>
  );
};

export default ConfirmationDetail;
